import * as Dialog from "@radix-ui/react-dialog";
import { useEnvironmentInterface } from "environment-interface";
import { useAddStatusNotification } from "features/notifications/hooks";
import { Stack, Text, styled, Button, Box } from "prism-react";

import { useAutoLaunchVSCode } from "hooks/useAutoLaunchVSCode";
import { useMissingVsCodeInsidersNotification } from "hooks/useMissingVsCodeInsidersNotification";
import { editorInfo, useOpenInVsCodeInline } from "hooks/useOpenInVsCode";
import type { OpenEditorType, VSCodeEditorArgs } from "utils/buildExtensionUri";
import { UnknownProtocolError } from "utils/url";

import { useGlobalModal } from "./useGlobalModal";
import type { UseGlobalModalProps } from "./useGlobalModal";

const InstructionContainer = styled(Stack, {
  background: "$neutral-bg-subtle",
  borderRadius: "$3",
  padding: "$10 $11",
});

const Instruction = styled(Text, {
  fontSize: "$sizes$4 !important",
  color: "$neutral-fg-subtle !important",

  counterIncrement: "list-item",
  "&:before": {
    content: "counter(list-item)",

    width: "$7",
    height: "$7",
    marginRight: "$3",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    color: "$neutral-bg-low",
    background: "$accent-primary-base",
    fontSize: "14px",
    borderRadius: "9999px",
  },
});

const Link = styled("a", {
  textDecoration: "underline !important",
  textDecorationThickness: "1px",
  textUnderlineOffset: "2px",
});

const VSCodePromptModal: React.FC<
  UseGlobalModalProps & {
    editor: VSCodeEditorArgs;
    activeFilepath: string | null;
    type: OpenEditorType;
  }
> = ({ close, editor, activeFilepath, type }) => {
  const { sentry } = useEnvironmentInterface();
  const [_, setAutoLaunchVSCode] = useAutoLaunchVSCode();
  const [openState, openInVsCode] = useOpenInVsCodeInline({
    editor,
    activeFilepath,
  });

  const dispatchNotification = useAddStatusNotification();
  const dispatchMissingVsCodeInsidersNotification =
    useMissingVsCodeInsidersNotification();

  const launchingVsCodeTitle = `Launching ${editorInfo[type].name}`;
  const launchInVsCodeButtonLabel = `Launch in ${editorInfo[type].name}`;

  const handleLaunchVscode = () => {
    const launch = () => {
      return openInVsCode({ type })
        .then(() => {
          // After the first time the modal is shown, subsequent invocations will auto-launch vs code
          setAutoLaunchVSCode(true);
          close();
        })
        .catch((error) => {
          if (error instanceof UnknownProtocolError) {
            dispatchMissingVsCodeInsidersNotification();
            return;
          }

          sentry.reportError(error);

          dispatchNotification({
            status: "error",
            message: "Failed to launch Visual Studio Code",
            actions: [
              {
                text: "Try again",
                onAction: launch,
              },
            ],
          });
        });
    };

    launch();
  };

  return (
    <>
      <Box
        css={{
          display: "flex",
          width: "100%",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Dialog.DialogClose asChild>
          <Button icon="close" onClick={close} />
        </Dialog.DialogClose>
      </Box>

      <Stack
        css={{
          height: "700px",
          alignItems: "center",
          justifyContent: "center",
        }}
        gap={0}
      >
        <Text color="accent-primary-base" size="md">
          Extension
        </Text>
        <Text size="xxl">{launchingVsCodeTitle}</Text>
        <Button
          css={{
            padding: "$6 $14",
            marginTop: "$10",
            flexWrap: "nowrap",
            fontSize: "$sizes$4",
          }}
          disabled={openState.state === "loading"}
          loading={openState.state === "loading"}
          onClick={handleLaunchVscode}
          variant="primary"
        >
          {launchInVsCodeButtonLabel}
        </Button>
        <Stack css={{ justifyContent: "center", marginTop: "$10" }} gap={4}>
          <Text
            css={{
              color: "$neutral-fg-subtle",
              textAlign: "center",
              fontSize: "$sizes$4",
            }}
          >
            Install the extension manually.
          </Text>
          <InstructionContainer gap={6}>
            <Instruction>
              Install{" "}
              <Link
                href={editorInfo[type].installUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                {editorInfo[type].name}
              </Link>
            </Instruction>
            <Instruction>
              Install{" "}
              <Link
                href="https://marketplace.visualstudio.com/items?itemName=CodeSandbox-io.codesandbox-projects"
                rel="noreferrer"
                target="_blank"
              >
                CodeSandbox Extension
              </Link>
            </Instruction>
            <Instruction>
              Sign in with your CodeSandbox account in VS Code
            </Instruction>
          </InstructionContainer>
        </Stack>
      </Stack>
    </>
  );
};

export const useVSCodePromptModal = () =>
  useGlobalModal(VSCodePromptModal, {
    maxWidth: 1024,
    closeOnInteractOutside: true,
  });
